import { http } from '@/api/client/httpClient';
import type { PagedResult } from '@/api/types/abp';
import type {
  FinkitTenantDto,
  ResetAdminPasswordDto,
  TenantClientInfoDto,
  TenantContactDetailCreateDto,
  TenantContactsDetailsDto,
  TenantSetupDto,
  TenantCreateDto,
  TenantDto,
  TenantInformationCreateDto,
  TenantListInput,
  TenantReportInformationUpdateDto,
  TenantRtExperienceDto,
  TenantSettingCreateDto,
  TenantUpdateDto,
} from '@/api/types/tenants';

// Finkit.TenantsManagement.FinkitTenantAppService (IFinkitTenantAppService):
//   GetListForDataTableAsync -> GET  /api/app/finkit-tenant/for-data-table
//   ResetAdminPasswordAsync  -> POST /api/app/finkit-tenant/{id}/reset-admin-password
const FINKIT_BASE = '/api/app/finkit-tenant';

// Volo.Abp.TenantManagement.TenantController (ITenantAppService):
//   [Route("api/multi-tenancy/tenants")] — GET/POST/PUT/DELETE {id}
//   default-connection-string: GET/PUT/DELETE {id}/default-connection-string
const ABP_BASE = '/api/multi-tenancy/tenants';

export const tenantsApi = {
  // ---- List (Finkit) ----
  async getListForDataTable(input: TenantListInput): Promise<PagedResult<FinkitTenantDto>> {
    const { data } = await http.get<PagedResult<FinkitTenantDto>>(`${FINKIT_BASE}/for-data-table`, {
      params: input,
    });
    return data;
  },

  // ---- Reset admin password (Finkit) ----
  async resetAdminPassword(id: string, input: ResetAdminPasswordDto): Promise<void> {
    await http.post(`${FINKIT_BASE}/${id}/reset-admin-password`, input);
  },

  // ---- CRUD (ABP) ----
  async get(id: string): Promise<TenantDto> {
    const { data } = await http.get<TenantDto>(`${ABP_BASE}/${id}`);
    return data;
  },

  async create(input: TenantCreateDto): Promise<TenantDto> {
    const { data } = await http.post<TenantDto>(ABP_BASE, input);
    return data;
  },

  async update(id: string, input: TenantUpdateDto): Promise<TenantDto> {
    const { data } = await http.put<TenantDto>(`${ABP_BASE}/${id}`, input);
    return data;
  },

  async remove(id: string): Promise<void> {
    await http.delete(`${ABP_BASE}/${id}`);
  },

  // ---- Default connection string (ABP) ----
  async getDefaultConnectionString(id: string): Promise<string> {
    const { data } = await http.get<string>(`${ABP_BASE}/${id}/default-connection-string`);
    return data;
  },

  async updateDefaultConnectionString(id: string, defaultConnectionString: string): Promise<void> {
    await http.put(`${ABP_BASE}/${id}/default-connection-string`, null, {
      params: { defaultConnectionString },
    });
  },

  async deleteDefaultConnectionString(id: string): Promise<void> {
    await http.delete(`${ABP_BASE}/${id}/default-connection-string`);
  },
};

// ---------------------------------------------------------------------------
// Edit tabs
// ---------------------------------------------------------------------------

// Finkit.Needs.Tenants.TenantSetupAppService (ApplicationService) — auto-exposed.
// Route base (ABP convention): /api/app/tenant-setup. Each setup method takes a
// trailing tenantId simple param, which ABP binds from the query string. Casing
// of the query param matches the C# signature (Tenantid vs tenantId).
const SETUP_BASE = '/api/app/tenant-setup';

export const tenantSetupApi = {
  async getInfo(tenantId: string): Promise<TenantInformationCreateDto> {
    const { data } = await http.get<TenantInformationCreateDto>(`${SETUP_BASE}/info/${tenantId}`);
    return data;
  },
  async getSetting(tenantId: string): Promise<TenantSettingCreateDto> {
    const { data } = await http.get<TenantSettingCreateDto>(`${SETUP_BASE}/setting/${tenantId}`);
    return data;
  },
  async getReports(tenantId: string): Promise<TenantReportInformationUpdateDto> {
    const { data } = await http.get<TenantReportInformationUpdateDto>(`${SETUP_BASE}/reports/${tenantId}`);
    return data;
  },
  async getDetails(tenantId: string): Promise<TenantContactDetailCreateDto[]> {
    const { data } = await http.get<TenantContactDetailCreateDto[]>(`${SETUP_BASE}/details/${tenantId}`);
    return data;
  },
  async getRtExperience(tenantId: string): Promise<TenantRtExperienceDto> {
    const { data } = await http.get<TenantRtExperienceDto>(`${SETUP_BASE}/rt-experience/${tenantId}`);
    return data;
  },
  async updateClientInfo(tenantId: string, input: TenantClientInfoDto): Promise<void> {
    // UpdateClientInfoAsync(TenantClientInfoDto infodto, Guid Tenantid) — Tenantid from query.
    await http.put(`${SETUP_BASE}/client-info`, input, { params: { Tenantid: tenantId } });
  },
  async updateReports(tenantId: string, input: TenantReportInformationUpdateDto): Promise<void> {
    // UpdateReportsAsync(TenantReportInformationUpdateDto input, Guid Tenantid) — Tenantid from query.
    await http.put(`${SETUP_BASE}/reports`, input, { params: { Tenantid: tenantId } });
  },
  async updateContactDetails(tenantId: string, input: TenantContactsDetailsDto): Promise<void> {
    await http.put(`${SETUP_BASE}/contact-details/${tenantId}`, input);
  },
  async updateRtExperience(tenantId: string, input: TenantRtExperienceDto): Promise<void> {
    await http.put(`${SETUP_BASE}/rt-experience/${tenantId}`, input);
  },
  // Setup wizard completion — inserts all sections + clears IsSetupPending (CreateAsync).
  async create(tenantId: string, input: TenantSetupDto): Promise<void> {
    await http.post(SETUP_BASE, input, { params: { tenantId } });
  },
};

// Finkit.TenantsManagement.ConnectionStrings.ConnectionStringsAppService.
// Route base: /api/app/connection-strings.
const CS_BASE = '/api/app/connection-strings';

export interface TenantConnectionStringRow {
  name: string;
  connectionString: string;
}

export const connectionStringsApi = {
  async getAll(tenantId: string): Promise<PagedResult<TenantConnectionStringRow>> {
    const { data } = await http.get<PagedResult<TenantConnectionStringRow>>(
      `${CS_BASE}/connection-string`,
      { params: { tenantId, maxResultCount: 1000, skipCount: 0 } },
    );
    return data;
  },
  async create(input: { tenantId: string; name: string; connectionString: string }): Promise<void> {
    await http.post(CS_BASE, input);
  },
  async update(input: { tenantId: string; name: string; connectionString: string }): Promise<void> {
    await http.put(CS_BASE, input);
  },
  async remove(tenantId: string, name: string): Promise<void> {
    await http.delete(CS_BASE, { params: { tenantId, name } });
  },
};

// Finkit.TenantsManagement.FinkitTenantEditAppService (white-listed IPs).
// Route base: /api/app/finkit-tenant-edit.
const WL_BASE = '/api/app/finkit-tenant-edit';

export interface TenantWhiteListedIpRow {
  id: number;
  ip: string;
}

export const whiteListedIpApi = {
  async getList(tenantId: string): Promise<PagedResult<TenantWhiteListedIpRow>> {
    const { data } = await http.get<PagedResult<TenantWhiteListedIpRow>>(
      `${WL_BASE}/white-listed-iPList-for-data-table`,
      { params: { tenantId, maxResultCount: 1000, skipCount: 0 } },
    );
    return data;
  },
  async create(input: { tenantId: string; ip: string }): Promise<void> {
    await http.post(WL_BASE, input);
  },
  async update(input: { tenantId: string; id: number; ip: string }): Promise<void> {
    await http.put(WL_BASE, input);
  },
  async remove(id: number, tenantId: string): Promise<void> {
    await http.delete(`${WL_BASE}/${id}`, { params: { TenantId: tenantId } });
  },
};

// Finkit.Tenants.Admin.JobToggles.JobToggleAdminAppService.
// Route base: /api/app/job-toggle-admin.
const JT_BASE = '/api/app/job-toggle-admin';

export interface JobToggleItem {
  itemKey: string;
  displayName: string;
  kind: string;
  dependsOn: string[];
  enabled: boolean;
}

export interface JobTogglesResult {
  items: JobToggleItem[];
}

export const jobTogglesApi = {
  async get(tenantId: string): Promise<JobTogglesResult> {
    const { data } = await http.get<JobTogglesResult>(JT_BASE, { params: { tenantId } });
    return data;
  },
  async save(tenantId: string, states: Record<string, boolean>): Promise<void> {
    await http.post(`${JT_BASE}/save`, { tenantId, states });
  },
};

// ASP.NET SelectListItem wire shape, returned by the country/currency services.
export interface MvcSelectListItem {
  value: string;
  text: string;
  selected?: boolean;
  disabled?: boolean;
  group?: unknown;
}

// Finkit.Common.Global.Country.CountryAppService -> /api/app/country.
// Finkit.Common.Global.Currency.CurrencyAppService -> /api/app/currency.
export const lookupApi = {
  async getCountries(tenantId: string): Promise<MvcSelectListItem[]> {
    const { data } = await http.get<MvcSelectListItem[]>(`/api/app/country/countries-select-list/${tenantId}`);
    return data;
  },
  async getCurrenciesWithSymbols(tenantId: string): Promise<MvcSelectListItem[]> {
    const { data } = await http.get<MvcSelectListItem[]>(
      `/api/app/currency/currencies-wit-symbols-select-list/${tenantId}`,
    );
    return data;
  },
};
